// This keeps a list of files that are being uploaded, and puts them in a context.
// A function to add files is also included.

// The other component in this feature, file upload state dialog, shows that state.

import { Text, useToast } from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";
import { IntlShape, useIntl } from "react-intl";

import { Upload } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { getErrorString, getErrors } from "@nens/ranalib";
import { displayBytes } from "@nens/shared-utils";

import { useInvalidateFilesAndHistoryQueries } from "@/queries/refetch-files-and-history";
import { containsInvalidCharacters, getFirstInvalidCharacter } from "@/utils/invalid-characters";

import { UploadFileState, UploadFilesArgs } from "../types/upload-types";

interface UpdateFileStateArgs {
  number: number;
  error?: string | null;
  progress?: number;
  isAborted?: boolean;
}

function constructFullPath(directory: string, fileName: string): string {
  if (directory === "/" || directory === "") return fileName;
  if (directory.endsWith("/")) return `${directory}${fileName}`;
  return `${directory}/${fileName}`;
}

function uploadFileToS3(
  file: File,
  fileUploadUrl: string,
  number: number,
  signal: AbortSignal,
  updateFileState: (update: UpdateFileStateArgs) => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xmlRequest = new XMLHttpRequest();

    // Handle abort signal
    signal.addEventListener(
      "abort",
      () => {
        xmlRequest.abort();
        reject();
      },
      { once: true }
    );

    // Track progress
    xmlRequest.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        updateFileState({ number, progress });
      }
    };

    // Handle upload completion
    xmlRequest.onload = () => {
      if (xmlRequest.status >= 200 && xmlRequest.status < 300) {
        resolve();
      } else {
        reject(new Error(`Failed to upload file. Status: ${xmlRequest.status}`));
      }
    };

    // Handle network errors
    xmlRequest.onerror = () => reject(new Error("Network error occurred during the upload."));

    // Initiate the upload
    xmlRequest.open("PUT", fileUploadUrl);
    xmlRequest.setRequestHeader("Content-Type", file.type); // Set content type
    xmlRequest.send(file);
  });
}

async function handleUploadError(
  error: unknown,
  intl: IntlShape,
  fileName: string
): Promise<string> {
  if (!error) {
    return intl.formatMessage({
      id: "error.file_upload",
      defaultMessage: "Something went wrong while uploading the file, please try again later.",
    });
  }
  // Generic upload error handling with filename context for type-based errors
  return getErrorString(await getErrors(error, intl, { filename: fileName }));
}

export function useFileUploadingProvider() {
  const intl = useIntl();
  const toast = useToast();
  const invalidateFilesAndHistoryQueries = useInvalidateFilesAndHistoryQueries();
  const [fileState, setFileState] = useState<UploadFileState>({
    nextNumber: 1,
    showFileModal: false,
    filesBeingUploaded: [],
  });
  const closeModalTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track callbacks per batch (all files uploaded in a single uploadFiles call)
  const batchCallbacks = useRef<
    Map<number, { callback: () => void; uploadNumbers: Set<number>; completedCount: number }>
  >(new Map());
  const nextBatchId = useRef(1);

  async function uploadFile(
    projectId: string,
    directory: string,
    file: File,
    dataType: string | undefined,
    number: number,
    signal: AbortSignal
  ) {
    const fullPath = constructFullPath(directory, file.name);

    let isAborted = false;
    let error: string | null = null;
    let response: Upload | null = null;

    try {
      // Step 1: Start the upload
      response = await getRanaApiV1().FilesApi.projectFileStartUpload({
        projectId,
        path: fullPath,
      });

      const { urls } = response;
      if (!urls || urls.length === 0) {
        throw new Error("No URLs returned for file upload.");
      }

      const fileUploadUrl = urls[0];

      // Step 2: Upload the file to the pre-signed URL
      await uploadFileToS3(file, fileUploadUrl, number, signal, updateFileState);

      // Step 3: Complete the upload process
      await getRanaApiV1().FilesApi.projectFileCompleteUpload({
        projectId,
        uploadComplete: { ...response, dataType },
      });
    } catch (e) {
      if (signal.aborted) {
        // If aborted, inform the backend and exit
        if (response)
          await getRanaApiV1().FilesApi.projectFileAbortUpload({ projectId, upload: response });
        isAborted = true;
      } else {
        // Handle upload errors (includes case-conflict detection)
        error = await handleUploadError(e, intl, file.name);
      }
    } finally {
      // Step 4: Update the file state and invalidate queries
      updateFileState({ number, error, isAborted });
      invalidateFilesAndHistoryQueries(projectId, fullPath);
    }
  }

  const updateFileState = useCallback(
    ({ number, error, progress, isAborted }: UpdateFileStateArgs) => {
      setFileState((fileState) => {
        const updatedFiles = fileState.filesBeingUploaded.map(
          (fileBeingUploaded): typeof fileBeingUploaded => {
            if (fileBeingUploaded.number !== number) return fileBeingUploaded;
            if (error) return { ...fileBeingUploaded, status: "error", error };
            if (progress !== undefined)
              return { ...fileBeingUploaded, status: "loading", progress };
            if (isAborted) return { ...fileBeingUploaded, status: "warning" };
            return { ...fileBeingUploaded, status: "success" };
          }
        );

        // Check if this upload completion triggers any batch callbacks
        const isUploadComplete =
          error !== undefined || isAborted || (progress === undefined && !error && !isAborted);

        if (isUploadComplete) {
          // Check all batches to see if any are now complete
          batchCallbacks.current.forEach((batch, batchId) => {
            const { uploadNumbers, callback } = batch;

            // If this upload number is in this batch
            if (uploadNumbers.has(number)) {
              // Count how many uploads in this batch are complete
              const completedCount = Array.from(uploadNumbers).filter((uploadNumber) => {
                const upload = updatedFiles.find((u) => u.number === uploadNumber);
                return upload && upload.status !== "loading";
              }).length;

              // If all uploads in the batch are complete, call the callback
              if (completedCount === uploadNumbers.size) {
                callback();
                batchCallbacks.current.delete(batchId);
              }
            }
          });
        }

        // If all files are now successful and modal is open, show toast and close modal after timeout
        if (
          updatedFiles.length > 0 &&
          updatedFiles.every((f) => f.status !== "loading") &&
          fileState.showFileModal
        ) {
          if (updatedFiles.some((f) => f.status === "success")) {
            toast({
              title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
              description: intl.formatMessage({
                id: "fileupload.success",
                defaultMessage: "Files uploaded successfully.",
              }),
            });
          }
          if (closeModalTimeout.current) clearTimeout(closeModalTimeout.current);

          closeModalTimeout.current = setTimeout(() => {
            setFileState((fs) => ({
              ...fs,
              showFileModal: false,
              filesBeingUploaded: fs.filesBeingUploaded.filter((f) => f.status === "loading"),
            }));
          }, 4000);
        }
        return {
          ...fileState,
          filesBeingUploaded: updatedFiles,
        };
      });
    },
    [intl, toast]
  );

  const uploadFiles = async (
    { projectId, uploads, withoutProgressDialog = false }: UploadFilesArgs,
    onComplete?: () => void
  ) => {
    // Flatten all uploads with their directories and validate each file
    const allFilesToUpload = uploads.flatMap(({ directory, files }) =>
      files.map(({ file, dataType }) => ({ directory, file, dataType }))
    );

    const filteredUploads = allFilesToUpload.filter(({ file }) => {
      const hasInvalidcharacter = containsInvalidCharacters(file.name);

      if (hasInvalidcharacter)
        toast({
          title: intl.formatMessage({ id: "error", defaultMessage: "Error!" }),
          description: intl.formatMessage(
            {
              id: "foldererrors.not_allowed_character",
              defaultMessage: `Character "{character}" in {fileName} is not allowed`,
            },
            { fileName: file.name, character: getFirstInvalidCharacter(file.name) }
          ),
          status: "error",
        });

      // 5 Gigabytes
      const MAX_SIZE = 5 * 1024 * 1024 * 1024;
      const tooBig = file.size > MAX_SIZE;

      if (tooBig)
        toast({
          duration: 10000,
          title: intl.formatMessage({ id: "error", defaultMessage: "Error!" }),
          description: intl.formatMessage(
            {
              id: "foldererrors.file_too_big",
              defaultMessage: `The file "{fileName}" exceeds the maximum allowed size. It is {fileSize}, but the limit is {maxSize}.`,
            },
            {
              fileName: file.name,
              fileSize: displayBytes(file.size),
              maxSize: displayBytes(MAX_SIZE),
            }
          ),
          status: "error",
        });

      return !hasInvalidcharacter && !tooBig;
    });

    if (filteredUploads.length > 0) {
      // Cancel closing modal if it was scheduled
      if (!withoutProgressDialog && closeModalTimeout.current) {
        clearTimeout(closeModalTimeout.current);
        closeModalTimeout.current = null;
      }

      // Create a batch ID for this upload batch
      const batchId = nextBatchId.current++;
      const uploadNumbers = new Set<number>();

      const hasShapeFile = filteredUploads.some(({ file }) =>
        file.name.toLowerCase().endsWith(".shp")
      );

      if (hasShapeFile) {
        toast({
          title: intl.formatMessage({
            id: "fileupload.shapefile_warning_title",
            defaultMessage: "You have added a shapefile",
          }),
          description: (
            <>
              <Text as="strong">
                {intl.formatMessage({
                  id: "fileupload.shapefile_warning_description",
                  defaultMessage:
                    "All uploaded Shapefiles are automatically converted to GeoPackage",
                })}
              </Text>{" "}
              <Text as="span">
                {intl.formatMessage({
                  id: "fileupload.shapefile_warning_description",
                  defaultMessage: "(GPKG) format to ensure compatibility with our platform",
                })}
              </Text>
            </>
          ),
          status: "info",
        });
      }

      // Update state once with all changes
      setFileState((fileState) => {
        let nextNumber = fileState.nextNumber;
        const newFiles = filteredUploads.map(({ directory, file, dataType }) => {
          const controller = new AbortController();
          const fileUpload = {
            controller,
            number: nextNumber,
            file,
            status: "loading" as const,
            withoutProgressDialog,
          };

          uploadNumbers.add(nextNumber);

          // Start the upload
          uploadFile(projectId, directory, file, dataType, nextNumber, controller.signal);
          nextNumber++;

          return fileUpload;
        });

        // Store callback for this batch if provided
        if (onComplete) {
          batchCallbacks.current.set(batchId, {
            callback: onComplete,
            uploadNumbers,
            completedCount: 0,
          });
        }

        return {
          ...fileState,
          showFileModal: !withoutProgressDialog || fileState.showFileModal,
          nextNumber,
          filesBeingUploaded: [...fileState.filesBeingUploaded, ...newFiles],
        };
      });
    }
  };

  const cancelUpload = (number: number) => {
    const fileToCancel = fileState.filesBeingUploaded.find((f) => f.number === number);
    if (!fileToCancel) return;
    fileToCancel.controller.abort();
    updateFileState({ number, isAborted: true });
  };

  return {
    ...fileState,
    cancelUpload,
    uploadFiles,
  };
}

export type UploadFilesFunc = ReturnType<typeof useFileUploadingProvider>["uploadFiles"];
